import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Clock, CreditCard, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { motion } from 'framer-motion';

export const QRPayment: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const qrCodeUrl = searchParams.get('qr_code_url');
  const expiresAt = parseInt(searchParams.get('expires_at') || '0');
  const amount = searchParams.get('amount');
  const paymentIntentId = searchParams.get('payment_intent_id');
  const orderDataString = searchParams.get('order_data');
  
  // Use the absolute expiry timestamp from URL params
  const [expiryTimestamp] = useState(expiresAt);
  const initialCountdown = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
  const [countdown, setCountdown] = useState(initialCountdown);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Check payment status periodically
  const checkPaymentStatus = async () => {
    if (!paymentIntentId || !orderDataString || checkingPayment) return;
    
    setCheckingPayment(true);
    try {
      const orderData = JSON.parse(orderDataString);
      
      const { data, error } = await supabase.functions.invoke('check-payment-status', {
        body: {
          payment_intent_id: paymentIntentId,
          order_data: orderData
        }
      });

      if (error) throw error;

      if (data.success && data.status === 'succeeded') {
        toast({
          title: "Payment Successful!",
          description: "Your order has been confirmed. Redirecting to confirmation page...",
        });
        
        // Redirect to order confirmation with order ID
        navigate(`/order-confirmation?order_id=${data.order_id}`);
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
    } finally {
      setCheckingPayment(false);
    }
  };

  // Download QR code function
  const downloadQRCode = async () => {
    if (!qrCodeUrl) return;
    
    try {
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      
      // Check if device is mobile and supports Web Share API
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile && navigator.share && navigator.canShare && navigator.canShare({ files: [new File([blob], 'qr-code.png', { type: 'image/png' })] })) {
        // Use Web Share API for mobile devices
        const file = new File([blob], `qr-payment-${Date.now()}.png`, { type: 'image/png' });
        
        await navigator.share({
          files: [file],
          title: 'QR Payment Code',
          text: 'Save this QR code to complete your payment'
        });
        
        toast({
          title: "QR Code Shared",
          description: "Save the QR code to your photos from the share menu",
        });
      } else {
        // Fallback to traditional download for desktop or unsupported devices
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `qr-payment-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast({
          title: "QR Code Downloaded",
          description: "The QR code has been saved to your device",
        });
      }
    } catch (error) {
      console.error('Download/Share failed:', error);
      toast({
        title: "Download Failed",
        description: "Could not download the QR code. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Function to calculate remaining time based on actual expiry timestamp
  const calculateRemainingTime = () => {
    const now = Date.now();
    const remaining = Math.max(0, Math.floor((expiryTimestamp - now) / 1000));
    return remaining;
  };

  useEffect(() => {
    if (!qrCodeUrl) {
      navigate(-1);
      return;
    }

    // Set up countdown timer that calculates based on actual time
    const timer = setInterval(() => {
      const remaining = calculateRemainingTime();
      setCountdown(remaining);
      
      if (remaining <= 0) {
        clearInterval(timer);
        toast({
          title: "QR Code Expired",
          description: "The QR code has expired. Please try again.",
          variant: "destructive",
        });
        navigate(-1);
      }
    }, 1000);

    // Set up payment status checking (every 5 seconds)
    const statusChecker = setInterval(checkPaymentStatus, 5000);

    // Handle page visibility changes to ensure accurate timing
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, update countdown immediately
        const remaining = calculateRemainingTime();
        setCountdown(remaining);
        
        if (remaining <= 0) {
          clearInterval(timer);
          clearInterval(statusChecker);
          toast({
            title: "QR Code Expired",
            description: "The QR code has expired. Please try again.",
            variant: "destructive",
          });
          navigate(-1);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(timer);
      clearInterval(statusChecker);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [qrCodeUrl, navigate, toast, paymentIntentId, orderDataString, expiryTimestamp]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (!qrCodeUrl) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Header />
      
      <main className="flex-grow relative overflow-hidden">
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-pink-900/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-pink-500/5 to-blue-500/5 animate-gradient-x" />
        
        <div className="relative z-10 container mx-auto px-4 py-8 pt-28">
          <div className="max-w-lg mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >

              <div className="text-center space-y-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="space-y-3 pt-8"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full border border-white/10 mb-4">
                    <CreditCard className="h-8 w-8 text-purple-400" />
                  </div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                    Scan QR Code to Pay
                  </h1>
                  <p className="text-gray-300 max-w-md mx-auto">
                    Open your banking app and scan this QR code to complete your payment securely
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="relative backdrop-blur-sm bg-white/10 p-8 rounded-2xl border border-white/20 shadow-2xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 rounded-2xl" />
                  <div className="relative space-y-4">
                    <img 
                      src={qrCodeUrl} 
                      alt="QRPh payment QR code" 
                      className="max-w-full h-auto mx-auto rounded-lg shadow-lg"
                    />
                    <Button
                      onClick={downloadQRCode}
                      variant="outline"
                      className="w-full border-white/60 text-white bg-white/5 hover:bg-white/20 hover:border-white/80"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download QR Code
                    </Button>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="backdrop-blur-sm bg-gradient-to-r from-amber-500/10 to-orange-500/10 p-6 rounded-xl border border-amber-500/20"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Clock className="h-5 w-5 text-amber-400" />
                    <p className="text-xl font-bold text-amber-400">
                      {formatTime(countdown)}
                    </p>
                  </div>
                  <p className="text-sm text-amber-300">
                    QR code expires in 30 minutes
                  </p>
                  {checkingPayment && (
                    <p className="text-xs text-gray-400 mt-2 animate-pulse">
                      Checking payment status...
                    </p>
                  )}
                </motion.div>

                {amount && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="backdrop-blur-sm bg-white/5 p-6 rounded-xl border border-white/10"
                  >
                    <p className="text-2xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                      ₱{parseInt(amount).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-400">
                      Once payment is complete, you'll receive your tickets via email ✨
                    </p>
                  </motion.div>
                )}
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="w-full"
                >
                  <Button
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    className="w-full sm:w-auto text-gray-300 hover:text-white border border-white/10 hover:bg-white/5"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                  className="text-xs text-gray-500 mt-4"
                >
                  Payment secured by QRPh • Your transaction is protected
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};